import React, { useState, useEffect, useRef } from "react";
import PlayerSelectionModal from "../components/PlayerSelectionModal.tsx";
import DatePickerModal from "../components/DatePickerModal.tsx";
import { getTodayCzech } from "../utils/date-utils.ts";
import {
  HandicapSettings,
  Player,
  RankingMode,
  Result,
  SavedTournament,
} from "../types.ts";
import {
  addResult,
  getAllResults,
  updatePlayer,
} from "../utils/firebase-utils.js";
import {
  ResultMatrix,
  areAllRoundsPlayed,
  calculateNewTimes,
  cropMatrix,
  expandToRows,
  formatResult,
  formatTime,
  generateTableColors,
  getActiveRows,
  invertResult,
  parseTime,
  timeToInput,
} from "../utils/tournament-calc.ts";
import {
  CRITERION_LABELS,
  RANKING_MODES,
  computeStandings,
  criterionValue,
} from "../utils/ranking.ts";

interface TournamentProps {
  playerCount: number;
  setPlayerCount: (count: number) => void;
  players: (Player | null)[];
  setPlayers: (players: (Player | null)[]) => void;
  results: Result[][];
  setResults: (results: Result[][]) => void;
  allPlayers: Player[];
  setAllPlayers: (players: Player[]) => void;
  handicapSettings: HandicapSettings;
  /** Režim hodnocení pořadí při rovnosti bodů. */
  rankingMode: RankingMode;
  /** Datum turnaje ve tvaru DD.MM.RRRR. */
  tournamentDate: string;
  setTournamentDate: (date: string) => void;
  /** Dvoukolový turnaj – každá dvojice hraje dvakrát. */
  doubleRound: boolean;
  setDoubleRound: (value: boolean) => void;
}

const RotatedHeaderCell = ({
  children,
  wide,
}: {
  children: React.ReactNode;
  /** Dvoukolový turnaj potřebuje v buňce místo na dva výsledky. */
  wide?: boolean;
}) => (
  <th
    className={`px-1 py-1 text-center font-medium text-gray-500 tracking-wider border ${
      wide ? "max-w-[52px] min-w-[52px]" : "max-w-[22px] min-w-[22px]"
    }`}
  >
    <div className="h-32 flex items-center justify-center">
      <div className="transform -rotate-90 whitespace-nowrap text-center">
        {children}
      </div>
    </div>
  </th>
);

const Tournament: React.FC<TournamentProps> = ({
  playerCount,
  setPlayerCount,
  players,
  setPlayers,
  results,
  setResults,
  allPlayers,
  setAllPlayers,
  handicapSettings,
  rankingMode,
  tournamentDate,
  setTournamentDate,
  doubleRound,
  setDoubleRound,
}) => {
  // Probíhající zápis – brání druhému kliknutí, než se uložení dokončí
  const [saving, setSaving] = useState<boolean>(false);
  // Datum turnaje, který už je uložený. Přežije i obnovení stránky, takže
  // po uložení nejde stejnou tabulku poslat do archivu podruhé.
  const [savedDate, setSavedDate] = useState<string | null>(() =>
    localStorage.getItem("savedTournamentDate")
  );

  const [showModal, setShowModal] = useState<boolean>(false);
  const [showDatePicker, setShowDatePicker] = useState<boolean>(false);
  const [currentCell, setCurrentCell] = useState<number | null>(null);
  // Editace času (handicapu) přímo v tabulce turnaje
  const [editingTimeRow, setEditingTimeRow] = useState<number | null>(null);
  const [timeDraft, setTimeDraft] = useState<string>("");
  const cancelTimeEdit = useRef<boolean>(false);

  // Upravení velikosti tabulky při změně počtu hráčů
  useEffect(() => {
    // Zachování existujících hráčů při zvýšení počtu hráčů
    if (players.length < playerCount) {
      // Zvětšení pole hráčů (přidání nových prázdných pozic)
      const newPlayers = [...players];
      while (newPlayers.length < playerCount) {
        newPlayers.push(null);
      }
      setPlayers(newPlayers);
    } else if (players.length > playerCount) {
      // Zmenšení pole hráčů (oříznutí)
      setPlayers(players.slice(0, playerCount));
    }

    // Zachování existujících výsledků při změně velikosti tabulky
    if (results.length > 0) {
      const newResults: Result[][] = [];

      // Kopírování existujících dat výsledků
      for (let i = 0; i < playerCount; i++) {
        newResults[i] = [];
        for (let j = 0; j < playerCount; j++) {
          if (i < results.length && j < results[i]?.length) {
            // Kopírování existujících výsledků
            newResults[i][j] = { ...results[i][j] };
          } else {
            // Inicializace nových buněk
            newResults[i][j] = { value: null, editable: true };
          }

          // Nastavení diagonály
          if (i === j) {
            newResults[i][j] = { value: null, editable: false };
          }
        }
      }

      setResults(newResults);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerCount]);

  const handlePlayerSelect = (playerId: string) => {
    if (currentCell !== null && playerId) {
      const selectedPlayer = allPlayers.find((p) => p.id === playerId);
      if (selectedPlayer) {
        const newPlayers = [...players];
        newPlayers[currentCell] = selectedPlayer;
        setPlayers(newPlayers);
        setShowModal(false);
        setCurrentCell(null);
      }
    }
  };

  // --- Editace času hráče -------------------------------------------------

  const startTimeEdit = (rowIndex: number) => {
    if (!players[rowIndex]) return;
    cancelTimeEdit.current = false;
    setEditingTimeRow(rowIndex);
    setTimeDraft(timeToInput(players[rowIndex]?.player_handicap));
  };

  const closeTimeEdit = () => {
    setEditingTimeRow(null);
    setTimeDraft("");
  };

  /**
   * Zapíše nový čas hráči – jak do právě rozehraného turnaje,
   * tak do jeho profilu (aktuální čas hráče ve Firebase).
   */
  const applyTime = (rowIndex: number, handicap: number) => {
    const player = players[rowIndex];
    if (!player) return;

    const updatedPlayer: Player = {
      ...player,
      player_handicap: handicap,
      player_updated: Date.now(),
    };

    // Turnajová sestava
    const newPlayers = [...players];
    newPlayers[rowIndex] = updatedPlayer;
    setPlayers(newPlayers);

    // Seznam všech hráčů (záložka Hráči)
    setAllPlayers(
      allPlayers.map((p) =>
        p.id === updatedPlayer.id
          ? {
              ...p,
              player_handicap: updatedPlayer.player_handicap,
              player_updated: updatedPlayer.player_updated,
            }
          : p
      )
    );

    // Trvalé uložení k hráči
    if (updatedPlayer.docId) {
      updatePlayer({ ...updatedPlayer }).catch((err: unknown) => {
        console.error("Chyba při ukládání času hráče:", err);
        alert("Čas se nepodařilo uložit k hráči. Zkuste to prosím znovu.");
      });
    } else {
      console.warn(
        "Hráč nemá docId, čas byl změněn pouze v turnaji:",
        updatedPlayer.player_name
      );
    }
  };

  const commitTimeEdit = () => {
    if (cancelTimeEdit.current) {
      cancelTimeEdit.current = false;
      closeTimeEdit();
      return;
    }
    if (editingTimeRow === null) return;

    const parsed = parseTime(timeDraft);
    if (parsed === null) {
      if (timeDraft.trim() !== "") {
        alert("Zadejte čas ve tvaru mm:ss (např. 5:30).");
      }
      closeTimeEdit();
      return;
    }

    applyTime(editingTimeRow, parsed);
    closeTimeEdit();
  };

  const handleTimeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitTimeEdit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelTimeEdit.current = true;
      closeTimeEdit();
    }
  };

  // -----------------------------------------------------------------------

  /**
   * Vyprázdní rozehraný turnaj – tabulku výsledků, sestavu hráčů i datum.
   * Prázdné `results` si App znovu nainicializuje ve správné velikosti.
   * Uložených turnajů ani hráčů se to netýká.
   */
  const handleCancelTournament = () => {
    if (
      !window.confirm(
        "Zrušit rozehraný turnaj?\n\n" +
          "Smaže se tabulka výsledků i sestava hráčů, datum se vrátí na " +
          "dnešek a vypne se dvoukolový režim. Už uložené turnaje a hráči " +
          "zůstanou beze změny."
      )
    ) {
      return;
    }

    closeTimeEdit();
    setPlayers(Array(playerCount).fill(null));
    setResults([]);
    setTournamentDate(getTodayCzech());
    setDoubleRound(false);
  };

  /**
   * Zapíše hráčům nově vypočítané časy (do seznamu hráčů i do Firebase).
   *
   * Čas v turnajové sestavě se záměrně nemění – ve sloupci "Čas" musí
   * zůstat čas, se kterým se hrálo. Díky tomu je opakované uložení
   * stejného turnaje idempotentní a čas se neodečte dvakrát.
   *
   * @returns jména hráčů, u kterých se uložení nepodařilo
   */
  const saveNewTimesToPlayers = async (): Promise<string[]> => {
    const failed: string[] = [];
    const updatedById: { [id: string]: number } = {};

    for (let i = 0; i < playerCount; i++) {
      const player = players[i];
      const newTime = newTimes[i];
      if (!player || newTime === null || newTime === undefined) continue;
      if (newTime === player.player_handicap) continue; // beze změny

      // Základem zápisu je aktuální záznam hráče, ne (možná starší) kopie
      // v turnajové sestavě – jinak bychom mohli přepsat jinde upravená data.
      const current = allPlayers.find((p) => p.id === player.id) ?? player;

      if (!current.docId) {
        console.warn(
          "Hráč nemá docId, nový čas nelze uložit:",
          player.player_name
        );
        failed.push(player.player_name);
        continue;
      }

      try {
        await updatePlayer({
          ...current,
          player_handicap: newTime,
          player_updated: Date.now(),
        });
        updatedById[player.id] = newTime;
      } catch (err) {
        console.error("Chyba při ukládání nového času hráče:", err);
        failed.push(player.player_name);
      }
    }

    if (Object.keys(updatedById).length > 0) {
      setAllPlayers(
        allPlayers.map((p) =>
          updatedById[p.id] !== undefined
            ? {
                ...p,
                player_handicap: updatedById[p.id],
                player_updated: Date.now(),
              }
            : p
        )
      );
    }

    return failed;
  };

  /** Zapamatuje si, že turnaj s tímhle datem je už v archivu. */
  const markSaved = (date: string) => {
    localStorage.setItem("savedTournamentDate", date);
    setSavedDate(date);
  };

  const handleSaveTournament = async () => {
    // Dvojí uložení by v archivu udělalo dva záznamy jednoho turnaje
    // a hráčům by se body za umístění započítaly dvakrát.
    if (saving || alreadySaved) return;
    setSaving(true);
    try {
      await saveTournament();
    } finally {
      setSaving(false);
    }
  };

  const saveTournament = async () => {
    // Turnaj se stejným datem už v archivu být nesmí. Kontrola jde proti
    // databázi, takže zachytí i uložení z jiného prohlížeče nebo zařízení.
    let existing: SavedTournament[] = [];
    try {
      existing = (await getAllResults()) as SavedTournament[];
    } catch (err) {
      console.error("Nepodařilo se ověřit, zda turnaj už není uložený:", err);
      alert(
        "Nepodařilo se ověřit, jestli už turnaj v archivu není. " +
          "Zkontrolujte připojení a zkuste to prosím znovu."
      );
      return;
    }

    const duplicate = existing.find((t) => t.date === tournamentDate);
    if (duplicate) {
      markSaved(tournamentDate);
      alert(
        `Turnaj s datem ${tournamentDate} už je v archivu uložený. ` +
          "Pokud ho chcete uložit znovu, smažte nejdřív ten původní " +
          'v záložce "Výsledky turnajů" (kliknutím na datum a tlačítkem Smazat turnaj).'
      );
      return;
    }

    // Ukládá se ořezaná tabulka – jen místa, na kterých opravdu někdo hrál.
    const tournamentData = {
      date: tournamentDate,
      playerCount: activeCount,
      player: activeRows.map((row) => players[row]?.id ?? null),
      // Jména a časy, se kterými hráči v tomto turnaji hráli
      playerNames: activeRows.map((row) => players[row]?.player_name ?? null),
      times: activeRows.map((row) => players[row]?.player_handicap ?? null),
      results: JSON.stringify(croppedMatrix),
      // Odveta se ukládá zvlášť; jednokolové turnaje pole vůbec nemají
      ...(doubleRound && croppedMatrix2
        ? { doubleRound: true, results2: JSON.stringify(croppedMatrix2) }
        : {}),
      points: croppedPoints,
      rankings: croppedRankings,
      // Pomocná hodnocení i režim, kterým se pořadí určilo – archiv se
      // pak zobrazuje přesně tak, jak byl turnaj uzavřen
      rankingMode,
      sonneborn: standings.sb,
      koya: standings.koya,
      wins: standings.wins,
      // Nové časy, které se hráčům po tomto turnaji zapsaly
      newTimes: activeRows.map((row) => newTimes[row] ?? null),
    };
    try {
      await addResult(tournamentData);
    } catch (err) {
      console.error("Chyba při ukládání turnaje:", err);
      alert("Turnaj se nepodařilo uložit. Zkuste to prosím znovu.");
      return;
    }

    markSaved(tournamentDate);

    // Nové časy se hráčům zapíší až po úspěšném uložení turnaje.
    const failed = await saveNewTimesToPlayers();

    // Uložení do localStorage pod unikátním klíčem
    const tournamentId = `tournament_${Date.now()}`;
    const savedTournaments = JSON.parse(
      localStorage.getItem("saved_tournaments") || "[]"
    );
    savedTournaments.push({ id: tournamentId, ...tournamentData });
    localStorage.setItem("saved_tournaments", JSON.stringify(savedTournaments));

    if (failed.length > 0) {
      alert(
        "Turnaj byl uložen, ale nový čas se nepodařilo uložit u hráčů: " +
          failed.join(", ")
      );
    } else {
      alert("Turnaj byl úspěšně uložen a hráčům byly zapsány nové časy!");
    }
  };

  const openPlayerSelection = (index: number) => {
    setCurrentCell(index);
    setShowModal(true);
  };

  /** Další hodnota v cyklu: null → 1 → ½ → 0 → null. */
  const nextResultValue = (current: number | null | undefined): number | null => {
    if (current === null || current === undefined) return 1;
    if (current === 1) return 0.5;
    if (current === 0.5) return 0;
    return null;
  };

  /**
   * Zápis výsledku partie. U dvoukolového turnaje se `game` rozlišuje,
   * zda jde o první partii, nebo o odvetu – každá se cykluje zvlášť.
   * Protějšek v zrcadlové buňce se vždy dopočítá jako opačný výsledek.
   */
  const handleResultClick = (row: number, col: number, game: 1 | 2 = 1) => {
    if (row === col) return; // Don't allow editing diagonal cells
    // Partii lze zapsat jen mezi dvěma vybranými hráči
    if (!players[row] || !players[col]) return;
    // Odvetu lze zadat jen ve dvoukolovém režimu
    if (game === 2 && !doubleRound) return;

    // Mělká kopie řádků; buňky se nahrazují novými objekty, nemutují se
    const newResults = results.map((r) => [...r]);

    if (!newResults[row] || !newResults[row][col]) {
      return;
    }

    const field: "value" | "value2" = game === 2 ? "value2" : "value";
    const nextValue = nextResultValue(newResults[row][col]?.[field]);

    // Rozprostřením se zachová výsledek té druhé partie
    newResults[row][col] = {
      ...newResults[row][col],
      [field]: nextValue,
    };
    newResults[col][row] = {
      ...newResults[col][row],
      [field]: invertResult(nextValue),
    };

    setResults(newResults);
  };

  /** Zapnutí/vypnutí dvoukolového režimu. */
  const handleToggleDoubleRound = () => {
    if (doubleRound) {
      const hasSecondRoundData = (results || []).some((row) =>
        (row || []).some(
          (cell) => cell && cell.value2 !== null && cell.value2 !== undefined
        )
      );

      if (
        hasSecondRoundData &&
        !window.confirm(
          "Vypnout dvoukolový turnaj?\n\nVýsledky odvet zůstanou uložené " +
            "v tabulce, ale nebudou se počítat ani ukládat."
        )
      ) {
        return;
      }
    }

    setDoubleRound(!doubleRound);
  };

  // Matice první partie a odvety zvlášť
  const matrix: ResultMatrix = (results || []).map((row) =>
    (row || []).map((cell) => (cell ? cell.value : null))
  );
  const matrix2: ResultMatrix = (results || []).map((row) =>
    (row || []).map((cell) => (cell ? cell.value2 ?? null : null))
  );

  const currentTimes = Array(playerCount)
    .fill(null)
    .map((_, i) => players[i]?.player_handicap ?? null);
  const assignedRows = Array(playerCount)
    .fill(false)
    .map((_, i) => Boolean(players[i]));

  // Turnaj se hraje jen mezi obsazenými místy – všechno se počítá
  // nad podtabulkou, aby prázdné řádky neředily body ani pořadí.
  const activeRows = getActiveRows(assignedRows, playerCount);
  const activeCount = activeRows.length;
  const croppedMatrix = cropMatrix(matrix, activeRows);
  const croppedMatrix2 = doubleRound ? cropMatrix(matrix2, activeRows) : null;

  // Pořadí podle zvoleného režimu hodnocení. Kola se dovnitř předávají zvlášť –
  // počet výher musí vidět jednotlivé partie, ne jen jejich součet. Pozice ve
  // výřezu odpovídá pořadí obsazených řádků, takže "příchod" sedí na tabulku.
  const standings = computeStandings(
    croppedMatrix2 ? [croppedMatrix, croppedMatrix2] : [croppedMatrix],
    activeCount,
    rankingMode
  );

  const croppedPoints = standings.points;
  const croppedRankings = standings.rankings;

  // Zpátky na indexy řádků tabulky (neobsazené řádky zůstanou prázdné)
  const points = expandToRows(croppedPoints, activeRows, playerCount);
  const rankings: { [key: number]: number } = {};
  // Pozice ve výřezu pro dohledání pomocných hodnocení daného řádku
  const cropIndexByRow: { [key: number]: number } = {};
  activeRows.forEach((row, index) => {
    rankings[row] = croppedRankings[index];
    cropIndexByRow[row] = index;
  });

  const colors = generateTableColors(playerCount);

  /**
   * Dvoukolově se hraje, když dorazí málo lidí. Rozhoduje počet obsazených
   * míst, ne velikost tabulky – ta bývá nastavená na plný počet. Přepínač
   * zůstává i při 8+ hráčích, pokud je režim zapnutý, aby šel vypnout.
   */
  const showDoubleRoundToggle = activeCount < 8 || doubleRound;
  // Turnaj jde uložit, jakmile jsou odehrány všechny partie mezi
  // přítomnými hráči – nedoražení lidé tabulku neblokují.
  // U dvoukolového turnaje musí být dohraná obě kola – součtová matice
  // by dvojici s jen jednou odehranou partií neodhalila.
  const allGamesPlayed =
    activeCount >= 2 &&
    areAllRoundsPlayed(
      croppedMatrix2 ? [croppedMatrix, croppedMatrix2] : [croppedMatrix],
      activeCount
    );

  // Turnaj s tímhle datem už je v archivu – uložit ho znovu by znamenalo
  // duplicitu, takže tlačítko zůstává vypnuté.
  const alreadySaved = savedDate === tournamentDate;

  // Nové časy podle pořadí. Počítají se až z kompletní tabulky –
  // do té doby by pořadí ještě nebylo konečné.
  const newTimes = allGamesPlayed
    ? calculateNewTimes(
        currentTimes,
        assignedRows,
        rankings,
        playerCount,
        handicapSettings
      )
    : Array(playerCount).fill(null);

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow">
        <button
          onClick={() => setShowDatePicker(true)}
          title="Změnit datum turnaje"
          className="inline-flex items-center gap-1 text-gray-600 hover:text-blue-600 hover:underline"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          {tournamentDate}
        </button>
        <button
          onClick={handleSaveTournament}
          disabled={!allGamesPlayed || saving || alreadySaved}
          title={
            alreadySaved
              ? `Turnaj ${tournamentDate} už je uložený v archivu`
              : undefined
          }
          className={`px-4 py-2 ml-4 mb-2 rounded-md ${
            allGamesPlayed && !saving && !alreadySaved
              ? "bg-green-600 text-white hover:bg-green-700"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
          }`}
        >
          {saving ? "Ukládám…" : alreadySaved ? "Uloženo" : "Uložit"}
        </button>
        <span className="ml-3 text-sm text-gray-500">
          Hodnocení:{" "}
          <span className="font-medium">
            {RANKING_MODES.find((m) => m.id === rankingMode)?.label ??
              rankingMode}
          </span>
          {allGamesPlayed && standings.usedCriteria.length > 0 && (
            <>
              {" · o pořadí rozhodlo: "}
              {standings.usedCriteria
                .map((c) => CRITERION_LABELS[c].full)
                .join(", ")}
            </>
          )}
        </span>
        {activeCount > 0 && activeCount < playerCount && (
          <span className="ml-3 text-sm text-gray-500">
            Obsazeno {activeCount} z {playerCount} míst – uloží se turnaj pro{" "}
            {activeCount} {activeCount >= 5 ? "hráčů" : "hráče"}.
          </span>
        )}
        <div className="float-end">
          <button
            onClick={handleCancelTournament}
            title="Vyprázdnit tabulku a začít nový turnaj"
            className={`px-4 py-2 ml-4 mb-2 rounded-md bg-orange-300 hover:bg-red-200 content-end`}
          >
            Zrušit
          </button>
        </div>
        {/* Dvoukolový režim – nabízí se jen u malých turnajů */}
        {showDoubleRoundToggle && (
          <div className="mb-3 flex items-center gap-3">
            <button
              type="button"
              role="switch"
              aria-checked={doubleRound}
              onClick={handleToggleDoubleRound}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                doubleRound ? "bg-blue-600" : "bg-gray-300"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  doubleRound ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
            <span className="text-sm text-gray-700">
              Dvoukolový turnaj
              <span className="text-gray-500">
                {" "}
                – každá dvojice hraje dvakrát (bílými i černými)
              </span>
            </span>
          </div>
        )}

        {/* Tournament table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 border text-sm">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider border w-10">
                  #
                </th>
                <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider border w-10">
                  Čas
                </th>
                <th
                  className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider border w-10"
                  title="Nový čas se hráčům uloží až při uložení turnaje"
                >
                  Nový čas
                </th>
                <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider border w-32">
                  Hráč
                </th>
                {Array(playerCount)
                  .fill(0)
                  .map((_, index) => (
                    <RotatedHeaderCell key={index} wide={doubleRound}>
                      {players[index]?.player_name || `${index + 1}`}
                    </RotatedHeaderCell>
                  ))}
                <th className="px-2 py-2 text-center font-medium text-gray-500 uppercase tracking-wider border">
                  Body
                </th>
                {standings.columns.map((criterion) => (
                  <th
                    key={criterion}
                    title={CRITERION_LABELS[criterion].full}
                    className="px-2 py-2 text-center font-medium text-gray-500 uppercase tracking-wider border"
                  >
                    {CRITERION_LABELS[criterion].short}
                  </th>
                ))}
                <th className="px-2 py-2 text-center font-medium text-gray-500 uppercase tracking-wider border">
                  Pořadí
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Array(playerCount)
                .fill(0)
                .map((_, rowIndex) => (
                  <tr
                    key={rowIndex}
                    className={`hover:bg-gray-50 ${
                      rankings[rowIndex] === 1 && allGamesPlayed
                        ? "row-gold"
                        : rankings[rowIndex] === 2 && allGamesPlayed
                        ? "row-silver"
                        : rankings[rowIndex] === 3 && allGamesPlayed
                        ? "row-bronze"
                        : ""
                    }`}
                  >
                    <td className="px-2 py-2 whitespace-nowrap border text-center">
                      {rowIndex + 1}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap border text-center">
                      {editingTimeRow === rowIndex ? (
                        <input
                          type="text"
                          autoFocus
                          value={timeDraft}
                          placeholder="mm:ss"
                          onChange={(e) => setTimeDraft(e.target.value)}
                          onBlur={commitTimeEdit}
                          onKeyDown={handleTimeKeyDown}
                          className="w-16 px-1 py-0.5 text-center border border-blue-400 rounded"
                        />
                      ) : players[rowIndex] ? (
                        <span
                          className="cursor-pointer hover:text-blue-600"
                          title="Klikněte pro úpravu času"
                          onClick={() => startTimeEdit(rowIndex)}
                        >
                          {formatTime(players[rowIndex]?.player_handicap)}
                        </span>
                      ) : (
                        <span className="text-gray-400">---</span>
                      )}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap border text-center">
                      {newTimes[rowIndex] !== null &&
                      newTimes[rowIndex] !== undefined ? (
                        <span
                          className={
                            newTimes[rowIndex] ===
                            players[rowIndex]?.player_handicap
                              ? "text-gray-500"
                              : (newTimes[rowIndex] as number) <
                                (players[rowIndex]?.player_handicap as number)
                              ? "font-bold text-green-600"
                              : "font-bold text-red-600"
                          }
                        >
                          {formatTime(newTimes[rowIndex])}
                        </span>
                      ) : (
                        <span className="text-gray-400">---</span>
                      )}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap border">
                      {players[rowIndex] ? (
                        <span
                          className="cursor-pointer hover:text-blue-600"
                          onDoubleClick={() => openPlayerSelection(rowIndex)}
                        >
                          {players[rowIndex]?.player_name}
                        </span>
                      ) : (
                        <button
                          onClick={() => openPlayerSelection(rowIndex)}
                          className="text-blue-600 hover:text-blue-800 font-medium"
                        >
                          Vybrat hráče
                        </button>
                      )}
                    </td>
                    {Array(playerCount)
                      .fill(0)
                      .map((_, colIndex) => {
                        const cell = results?.[rowIndex]?.[colIndex];
                        const playable =
                          rowIndex !== colIndex &&
                          Boolean(players[rowIndex]) &&
                          Boolean(players[colIndex]);

                        return (
                          <td
                            key={colIndex}
                            className={`whitespace-nowrap text-center border ${
                              colors[rowIndex][colIndex]
                            } ${
                              !players[rowIndex] || !players[colIndex]
                                ? "cursor-default opacity-40"
                                : rowIndex === colIndex
                                ? "cursor-default"
                                : "cursor-pointer"
                            } ${doubleRound ? "p-0" : "px-2 py-2"}`}
                            onClick={
                              doubleRound
                                ? undefined
                                : () => handleResultClick(rowIndex, colIndex)
                            }
                          >
                            {doubleRound ? (
                              // Každá polovina buňky je klikací celá – vlevo
                              // první partie, vpravo odveta
                              <div className="flex items-stretch">
                                <button
                                  type="button"
                                  disabled={!playable}
                                  title={playable ? "První partie" : undefined}
                                  onClick={() =>
                                    handleResultClick(rowIndex, colIndex, 1)
                                  }
                                  className={`flex-1 px-1 py-2 font-extrabold ${
                                    playable
                                      ? "cursor-pointer hover:bg-blue-100"
                                      : "cursor-default"
                                  }`}
                                >
                                  {formatResult(cell?.value)}
                                </button>
                                <button
                                  type="button"
                                  disabled={!playable}
                                  title={playable ? "Odveta" : undefined}
                                  onClick={() =>
                                    handleResultClick(rowIndex, colIndex, 2)
                                  }
                                  className={`flex-1 px-1 py-2 font-extrabold border-l border-gray-300 ${
                                    playable
                                      ? "cursor-pointer hover:bg-blue-100"
                                      : "cursor-default"
                                  }`}
                                >
                                  {formatResult(cell?.value2)}
                                </button>
                              </div>
                            ) : (
                              <span className="font-extrabold">
                                {formatResult(cell?.value)}
                              </span>
                            )}
                          </td>
                        );
                      })}
                    <td className="px-2 py-2 whitespace-nowrap border text-center font-bold">
                      {points[rowIndex] !== undefined ? points[rowIndex] : ""}
                    </td>
                    {standings.columns.map((criterion) => (
                      <td
                        key={criterion}
                        className="px-2 py-2 whitespace-nowrap border text-center"
                      >
                        {cropIndexByRow[rowIndex] !== undefined
                          ? criterionValue(
                              standings,
                              criterion,
                              cropIndexByRow[rowIndex]
                            )
                          : ""}
                      </td>
                    ))}
                    <td
                      className={`px-2 py-2 whitespace-nowrap border text-center ${
                        rankings[rowIndex] === 1 && allGamesPlayed === true
                          ? "gold-cup"
                          : ""
                      }`}
                    >
                      {rankings[rowIndex] !== undefined
                        ? rankings[rowIndex]
                        : ""}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Výběr data turnaje */}
      {showDatePicker && (
        <DatePickerModal
          value={tournamentDate}
          onSelect={(date) => {
            setTournamentDate(date);
            setShowDatePicker(false);
          }}
          onClose={() => setShowDatePicker(false)}
        />
      )}

      {/* Player selection modal */}
      {showModal && (
        <PlayerSelectionModal
          players={allPlayers.filter(
            (player) => !players.some((p) => p?.id === player.id)
          )}
          onSelect={handlePlayerSelect}
          onClose={() => {
            setShowModal(false);
            setCurrentCell(null);
          }}
        />
      )}
    </div>
  );
};

export default Tournament;
